import * as THREE from 'three';
import type { PixelPosition } from '../../tilemap/types';
import {
  CAMERA_LOOK_SMOOTHING,
  CAMERA_ORBIT_STEP,
  CAMERA_SETTLE_EPSILON,
  CAMERA_TILT_STEP,
  DEFAULT_CAMERA_AZIMUTH,
  DEFAULT_CAMERA_DISTANCE,
  DEFAULT_CAMERA_TILT,
  MAX_CAMERA_TILT,
  MIN_CAMERA_TILT,
  PERSPECTIVE_FOV_DEGREES,
  type OrbitDirection,
  type ProjectionController,
} from './ProjectionController';

const MIN_ORTHOGRAPHIC_ZOOM = 0.55;
const MAX_ORTHOGRAPHIC_ZOOM = 2.4;
const ORTHOGRAPHIC_ZOOM_STEP = 0.0015;

export class OrthographicProjectionController implements ProjectionController {
  public readonly camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 300);

  private readonly cameraTarget = new THREE.Vector3();
  private readonly desiredCameraTarget = new THREE.Vector3();
  private viewportWidth = 1;
  private viewportHeight = 1;
  private cameraDistance = DEFAULT_CAMERA_DISTANCE;
  private cameraAzimuth = DEFAULT_CAMERA_AZIMUTH;
  private cameraTilt = DEFAULT_CAMERA_TILT;
  private zoom = 1;

  public syncViewport(width: number, height: number): void {
    this.viewportWidth = width;
    this.viewportHeight = height;
    this.updateFrustum();
  }

  public updateCamera(target: THREE.Vector3): boolean {
    this.desiredCameraTarget.copy(target);
    let animating = false;
    if (this.cameraTarget.lengthSq() === 0) {
      this.cameraTarget.copy(this.desiredCameraTarget);
    } else {
      animating = this.cameraTarget.distanceToSquared(this.desiredCameraTarget) > CAMERA_SETTLE_EPSILON * CAMERA_SETTLE_EPSILON;
      this.cameraTarget.lerp(this.desiredCameraTarget, CAMERA_LOOK_SMOOTHING);
      if (!animating && this.cameraTarget.distanceToSquared(this.desiredCameraTarget) > 0) {
        this.cameraTarget.copy(this.desiredCameraTarget);
      }
    }

    const horizontalDistance = Math.cos(this.cameraTilt) * this.cameraDistance;
    const cameraOffset = new THREE.Vector3(
      Math.cos(this.cameraAzimuth) * horizontalDistance,
      Math.sin(this.cameraTilt) * this.cameraDistance,
      Math.sin(this.cameraAzimuth) * horizontalDistance,
    );
    this.camera.position.copy(this.cameraTarget).add(cameraOffset);
    this.camera.lookAt(this.cameraTarget);
    this.updateFrustum();
    return animating;
  }

  public snapToTarget(target: THREE.Vector3): void {
    this.desiredCameraTarget.copy(target);
    this.cameraTarget.copy(target);
    const horizontalDistance = Math.cos(this.cameraTilt) * this.cameraDistance;
    const cameraOffset = new THREE.Vector3(
      Math.cos(this.cameraAzimuth) * horizontalDistance,
      Math.sin(this.cameraTilt) * this.cameraDistance,
      Math.sin(this.cameraAzimuth) * horizontalDistance,
    );
    this.camera.position.copy(this.cameraTarget).add(cameraOffset);
    this.camera.lookAt(this.cameraTarget);
    this.updateFrustum();
  }

  public rotateOrbit(direction: OrbitDirection): void {
    const delta = direction === 'left' ? -CAMERA_ORBIT_STEP : CAMERA_ORBIT_STEP;
    this.cameraAzimuth = THREE.MathUtils.euclideanModulo(this.cameraAzimuth + delta, Math.PI * 2);
  }

  public adjustZoom(deltaY: number): boolean {
    const nextZoom = THREE.MathUtils.clamp(
      this.zoom * (1 - deltaY * ORTHOGRAPHIC_ZOOM_STEP),
      MIN_ORTHOGRAPHIC_ZOOM,
      MAX_ORTHOGRAPHIC_ZOOM,
    );
    if (Math.abs(nextZoom - this.zoom) < 1e-5) {
      return false;
    }
    this.zoom = nextZoom;
    this.updateFrustum();
    return true;
  }

  public adjustTilt(deltaY: number): boolean {
    const nextTilt = THREE.MathUtils.clamp(
      this.cameraTilt - deltaY * CAMERA_TILT_STEP,
      MIN_CAMERA_TILT,
      MAX_CAMERA_TILT,
    );
    if (Math.abs(nextTilt - this.cameraTilt) < 1e-5) {
      return false;
    }
    this.cameraTilt = nextTilt;
    return true;
  }

  public setRayFromScreen(
    screenX: number,
    screenY: number,
    canvasWidth: number,
    canvasHeight: number,
    raycaster: THREE.Raycaster,
    ndcPointer: THREE.Vector2,
  ): void {
    ndcPointer.set(
      (screenX / canvasWidth) * 2 - 1,
      -(screenY / canvasHeight) * 2 + 1,
    );
    raycaster.setFromCamera(ndcPointer, this.camera);
  }

  public projectWorldPoint(
    worldX: number,
    worldY: number,
    worldZ: number,
    canvasWidth: number,
    canvasHeight: number,
    scratch: THREE.Vector3,
  ): PixelPosition {
    scratch.set(worldX, worldY, worldZ).project(this.camera);
    return {
      x: (scratch.x * 0.5 + 0.5) * canvasWidth,
      y: (-scratch.y * 0.5 + 0.5) * canvasHeight,
    };
  }

  private updateFrustum(): void {
    const aspect = this.viewportWidth / Math.max(1, this.viewportHeight);
    const baseHeight =
      2 * Math.tan(THREE.MathUtils.degToRad(PERSPECTIVE_FOV_DEGREES) / 2) * this.cameraDistance;
    const frustumHeight = baseHeight / this.zoom;
    const frustumWidth = frustumHeight * aspect;
    this.camera.left = -frustumWidth / 2;
    this.camera.right = frustumWidth / 2;
    this.camera.top = frustumHeight / 2;
    this.camera.bottom = -frustumHeight / 2;
    this.camera.updateProjectionMatrix();
  }
}
